import { useState, useRef } from 'react';
import { X, Camera, Upload, Globe, Users, Lock, Music, Scissors, Sparkles } from 'lucide-react';
import clsx from 'clsx';
import { mediaApi, validateMediaBeforeUpload, MediaValidationError, MEDIA_LIMITS } from '@/api/media';
import { storiesApi } from '@/api/stories';
import { draftsApi } from '@/api/storyDrafts';
import type { MusicTrack } from '@/api/music';
import MusicPicker from './MusicPicker';
import VideoTrimmer from './VideoTrimmer';
import AiVideoPanel from './AiVideoPanel';

interface Props {
  onClose: () => void;
  onCreated: () => void;
  parentStoryId?: string;
  replyMode?: 'DUET' | 'STITCH';
  initialDraftId?: string;
}

const PRIVACY_OPTIONS = [
  { value: 'ALL' as const, label: 'Все', icon: Globe },
  { value: 'CONTACTS' as const, label: 'Друзья', icon: Users },
  { value: 'NOBODY' as const, label: 'Только я', icon: Lock },
];

const ACCEPT_TYPES = 'image/jpeg,image/png,image/webp,image/gif,video/mp4,video/quicktime,video/webm';

export default function StoryCreateModal({ onClose, onCreated, parentStoryId, replyMode }: Props) {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [isVideo, setIsVideo] = useState(false);
  const [caption, setCaption] = useState('');
  const [privacy, setPrivacy] = useState<'ALL' | 'CONTACTS' | 'NOBODY'>('ALL');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [musicTrack, setMusicTrack] = useState<MusicTrack | null>(null);
  const [musicOpen, setMusicOpen] = useState(false);
  const [trimmerOpen, setTrimmerOpen] = useState(false);
  const [tab, setTab] = useState<'upload' | 'ai'>('upload');
  const abortRef = useRef<AbortController | null>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const galleryRef = useRef<HTMLInputElement>(null);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    e.target.value = '';
    try {
      await validateMediaBeforeUpload(f);
    } catch (err) {
      if (err instanceof MediaValidationError) {
        setError(err.message);
      } else {
        setError('Файл не подходит');
      }
      return;
    }
    setFile(f);
    setIsVideo(f.type.startsWith('video/'));
    setPreview(URL.createObjectURL(f));
    setError(null);
  };

  const clearFile = () => {
    if (preview) URL.revokeObjectURL(preview);
    setFile(null);
    setPreview(null);
    setIsVideo(false);
  };

  const handleSubmit = async () => {
    if (!file) return;
    setUploading(true);
    setUploadProgress(0);
    setError(null);
    abortRef.current = new AbortController();
    try {
      const media = await mediaApi.upload(file, {
        onProgress: setUploadProgress,
        signal: abortRef.current.signal,
      });
      await storiesApi.create({
        mediaFileId: media.id,
        caption: caption.trim() || undefined,
        privacy,
        musicTrackId: musicTrack?.id,
        parentStoryId,
        replyMode,
      });
      onCreated();
    } catch (err: any) {
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') {
        setError('Загрузка отменена');
      } else {
        setError(err?.response?.data?.message ?? err?.message ?? 'Ошибка загрузки');
      }
    } finally {
      setUploading(false);
      setUploadProgress(0);
      abortRef.current = null;
    }
  };

  const cancelUpload = () => {
    abortRef.current?.abort();
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md bg-surface-900 border border-surface-700 rounded-xl shadow-2xl animate-fade-in">
        <div className="flex items-center justify-between px-5 py-4 border-b border-surface-800">
          <h3 className="font-semibold text-surface-50">
            {parentStoryId
              ? (replyMode === 'STITCH' ? 'Stitch — ответ видео' : 'Duet — ответ видео')
              : 'Новая история'}
          </h3>
          <button onClick={onClose} className="btn-ghost p-1">
            <X size={16} />
          </button>
        </div>

        {!parentStoryId && (
          <div className="flex gap-1 px-5 pt-3">
            <button
              onClick={() => setTab('upload')}
              className={clsx(
                'flex-1 py-1.5 rounded-lg text-xs font-medium transition-colors',
                tab === 'upload' ? 'bg-surface-800 text-surface-100' : 'text-surface-400 hover:text-surface-200',
              )}
            >
              Загрузить
            </button>
            <button
              onClick={() => setTab('ai')}
              className={clsx(
                'flex-1 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center justify-center gap-1',
                tab === 'ai' ? 'bg-surface-800 text-brand-300' : 'text-surface-400 hover:text-surface-200',
              )}
            >
              <Sparkles size={12} /> AI-видео
            </button>
          </div>
        )}

        {tab === 'ai' && !parentStoryId ? (
          <div className="p-5">
            <AiVideoPanel onPublished={onCreated} />
          </div>
        ) : (
        <div className="p-5 space-y-4">
          {/* Media pick */}
          {!preview ? (
            <>
              <div className="flex gap-3">
                <button
                  onClick={() => cameraRef.current?.click()}
                  className="flex-1 h-36 border-2 border-dashed border-surface-600 rounded-xl flex flex-col items-center justify-center gap-2 text-surface-400 hover:border-brand-500 hover:text-brand-400 transition-colors"
                >
                  <Camera size={28} />
                  <span className="text-xs">Камера</span>
                </button>
                <button
                  onClick={() => galleryRef.current?.click()}
                  className="flex-1 h-36 border-2 border-dashed border-surface-600 rounded-xl flex flex-col items-center justify-center gap-2 text-surface-400 hover:border-brand-500 hover:text-brand-400 transition-colors"
                >
                  <Upload size={28} />
                  <span className="text-xs">Галерея</span>
                </button>
              </div>
              <p className="text-[11px] text-surface-500 mt-1">
                Фото до {MEDIA_LIMITS.maxImageMB} МБ · видео до {MEDIA_LIMITS.maxVideoMB} МБ и {Math.round(MEDIA_LIMITS.maxVideoDurationSec / 60)} мин
              </p>
            </>
          ) : (
            <div className="relative">
              {isVideo ? (
                <video
                  src={preview}
                  className="w-full max-h-64 object-contain rounded-xl bg-black"
                  controls
                  muted
                />
              ) : (
                <img
                  src={preview}
                  alt="Preview"
                  className="w-full max-h-64 object-contain rounded-xl bg-black"
                />
              )}
              <button
                onClick={clearFile}
                className="absolute top-2 right-2 p-1 bg-black/50 rounded-full text-white hover:bg-black/70"
              >
                <X size={16} />
              </button>
            </div>
          )}
          {/* Camera input — capture opens device camera on mobile, file picker on desktop */}
          <input
            ref={cameraRef}
            type="file"
            accept="image/*,video/*"
            capture="environment"
            onChange={handleFile}
            className="hidden"
          />
          {/* Gallery input — no capture, opens file picker / gallery */}
          <input
            ref={galleryRef}
            type="file"
            accept={ACCEPT_TYPES}
            onChange={handleFile}
            className="hidden"
          />

          {/* Caption */}
          <input
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder="Подпись (необязательно)"
            maxLength={200}
            className="w-full bg-surface-800 border border-surface-700 rounded-lg px-3 py-2 text-sm text-surface-100 placeholder-surface-500 outline-none focus:border-brand-500"
          />

          {/* Video edit (only if we have a video file) */}
          {file && isVideo && (
            <button
              onClick={() => setTrimmerOpen(true)}
              disabled={uploading}
              className="w-full flex items-center gap-2 bg-surface-800 border border-surface-700 rounded-lg px-3 py-2 text-sm text-left hover:bg-surface-750 disabled:opacity-40"
            >
              <Scissors size={16} className="text-surface-400" />
              <span className="flex-1">Обрезать, добавить текст, изменить скорость</span>
            </button>
          )}

          {/* Music */}
          <button
            onClick={() => setMusicOpen(true)}
            className="w-full flex items-center gap-2 bg-surface-800 border border-surface-700 rounded-lg px-3 py-2 text-sm text-left hover:bg-surface-750"
          >
            <Music size={16} className="text-surface-400" />
            <span className="flex-1 truncate">
              {musicTrack ? `${musicTrack.artist} — ${musicTrack.title}` : 'Добавить музыку'}
            </span>
            {musicTrack && (
              <X
                size={14}
                className="text-surface-400"
                onClick={(e) => { e.stopPropagation(); setMusicTrack(null); }}
              />
            )}
          </button>

          {/* Privacy */}
          <div className="flex gap-2">
            {PRIVACY_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setPrivacy(opt.value)}
                className={clsx(
                  'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs transition-colors',
                  privacy === opt.value
                    ? 'bg-brand-600 text-white'
                    : 'bg-surface-800 text-surface-400 hover:bg-surface-700',
                )}
              >
                <opt.icon size={14} />
                {opt.label}
              </button>
            ))}
          </div>

          {error && <p className="text-red-400 text-xs">{error}</p>}

          {uploading && (
            <div className="space-y-1">
              <div className="flex items-center justify-between text-xs text-surface-400">
                <span>Загрузка… {uploadProgress}%</span>
                <button
                  onClick={cancelUpload}
                  className="text-red-400 hover:text-red-300"
                >
                  Отменить
                </button>
              </div>
              <div className="w-full h-1.5 bg-surface-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-brand-500 transition-all"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          )}

          <div className="flex gap-2">
            <button
              onClick={handleSubmit}
              disabled={!file || uploading}
              className="flex-1 py-2 bg-brand-600 hover:bg-brand-500 disabled:opacity-40 rounded-lg text-white text-sm font-medium transition-colors"
            >
              {uploading ? 'Загрузка…' : 'Опубликовать'}
            </button>
            {!parentStoryId && (
              <button
                disabled={!file || uploading}
                onClick={async () => {
                  if (!file) return;
                  setUploading(true);
                  try {
                    const media = await mediaApi.upload(file);
                    await draftsApi.upsert({
                      mediaFileId: media.id,
                      caption: caption.trim() || null,
                      privacy,
                      musicTrackId: musicTrack?.id ?? null,
                    });
                    onCreated();
                  } catch (err: any) {
                    setError(err?.response?.data?.message ?? err?.message ?? 'Ошибка');
                  } finally {
                    setUploading(false);
                  }
                }}
                className="px-3 py-2 bg-surface-800 hover:bg-surface-700 disabled:opacity-40 rounded-lg text-surface-200 text-sm"
                title="Сохранить как черновик (без публикации)"
              >
                Draft
              </button>
            )}
          </div>
        </div>
        )}
      </div>
      {musicOpen && (
        <MusicPicker
          value={musicTrack?.id ?? null}
          onChange={setMusicTrack}
          onClose={() => setMusicOpen(false)}
        />
      )}

      {trimmerOpen && file && isVideo && (
        <VideoTrimmer
          srcFile={file}
          onCancel={() => setTrimmerOpen(false)}
          onDone={(edited) => {
            // Заменяем оригинал на отредактированный файл, обновляем preview.
            if (preview) URL.revokeObjectURL(preview);
            setFile(edited);
            setPreview(URL.createObjectURL(edited));
            setTrimmerOpen(false);
          }}
        />
      )}
    </div>
  );
}
